import {
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type CSSProperties,
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
} from 'react';
import { cn } from '~/lib/cn';

/**
 * Shared horizontal strip used by the stats strip, the send-money currency-rate
 * strip, and the crypto providers strip. Encapsulates the band treatment
 * (top/bottom black-6 borders), the vertical `before:` dividers between items
 * (none before the first), and — in the default `scroll` layout — the
 * fixed-width, drag-scrollable, cropped-overflow track with a hidden scrollbar.
 *
 * Item content is supplied via `renderItem`, so each page provides its own data
 * shape and markup while the scroll/drag/divider logic lives here once.
 *
 * Layouts:
 *  - `scroll` (default): fixed-width items in a drag-scroll track that crops the
 *    last item at the edge (stats + currency rates).
 *  - `spread`: items wrap/centre on mobile and justify-between on desktop, with
 *    desktop-only dividers (crypto providers strip).
 *
 * When `autoScroll` is set, the `scroll` track becomes a seamless marquee: the
 * item list is rendered N× (enough to overflow the viewport plus one full set)
 * and a CSS `translate3d` keyframe animation drifts the track, wrapping by
 * exactly one item set. Programmatic `scrollLeft` is avoided — iOS Safari often
 * ignores rAF `scrollLeft` updates, which made the strip look frozen on real
 * phones while desktop/Chromium previews still animated.
 * `direction` chooses which way it drifts (`'rtl'` — the default — moves content
 * right→left; `'ltr'` moves it left→right). Pointer/wheel interaction pauses the
 * motion; it resumes 2s after the strip has been left idle.
 * `prefers-reduced-motion: reduce` disables the animation entirely (leaving a
 * plain single-set drag-scroll strip).
 */
type StripLayout = 'scroll' | 'spread';
type ScrollDirection = 'rtl' | 'ltr';

interface ScrollStripProps<T> {
  items: T[];
  renderItem: (item: T) => ReactNode;
  getKey: (item: T, index: number) => string;
  /** Per-item classes (e.g. fixed width + inner padding + gap). */
  itemClassName?: string;
  layout?: StripLayout;
  /** Opt into the seamless auto-scrolling marquee (scroll layout). */
  autoScroll?: boolean;
  /**
   * Like {@link autoScroll} but gated to mobile widths (< md): the marquee only
   * runs below the `md` breakpoint and the strip stays static on desktop. Used
   * by the compact mobile stats / providers rows.
   */
  autoScrollMobileOnly?: boolean;
  /** Marquee drift direction (only used with `autoScroll`). Defaults to `'rtl'`. */
  direction?: ScrollDirection;
  /**
   * Drop the band chrome (top/bottom borders + per-item vertical dividers) for a
   * clean, card-style marquee. Defaults to `false` (keeps the strip treatment).
   */
  plain?: boolean;
  className?: string;
}

/** Pointer travel (px) below which a press counts as a tap, not a drag. */
const TAP_THRESHOLD = 5;
/** Marquee speed in px/s — slow enough to read as an ambient drift. */
const AUTO_SCROLL_SPEED = 40;
/** How long the strip must stay idle before auto-scroll resumes (ms). */
const RESUME_DELAY = 2000;

/** SSR-safe `prefers-reduced-motion: reduce` subscription. */
function usePrefersReducedMotion() {
  return useSyncExternalStore(
    (onChange) => {
      if (typeof window === 'undefined' || !window.matchMedia) return () => {};
      const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    },
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    () => false,
  );
}

/**
 * SSR-safe "is the viewport below the `md` breakpoint (768px)" subscription.
 * Server snapshot is `false` (desktop-first), so mobile-only marquees hydrate
 * as static and switch on after mount — no hydration mismatch.
 */
function useIsBelowMd() {
  return useSyncExternalStore(
    (onChange) => {
      if (typeof window === 'undefined' || !window.matchMedia) return () => {};
      const mq = window.matchMedia('(max-width: 767.98px)');
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    },
    () => window.matchMedia('(max-width: 767.98px)').matches,
    () => false,
  );
}

export function ScrollStrip<T>({
  items,
  renderItem,
  getKey,
  itemClassName,
  layout = 'scroll',
  autoScroll = false,
  autoScrollMobileOnly = false,
  direction = 'rtl',
  plain = false,
  className,
}: ScrollStripProps<T>) {
  const scrollRef = useRef<HTMLUListElement>(null);
  // `moved` records whether the pointer travelled past the tap threshold; a
  // moved press is a drag, so we suppress the click it would otherwise fire.
  const drag = useRef({ down: false, startX: 0, startLeft: 0, moved: false });
  const offsetRef = useRef<HTMLDivElement>(null);
  const offset = useRef(0);
  const isScroll = layout === 'scroll';

  const reducedMotion = usePrefersReducedMotion();
  const isBelowMd = useIsBelowMd();
  // `autoScroll` marquees at every width; `autoScrollMobileOnly` only below md
  // (desktop stays a static, drag-scrollable strip). Reduced-motion disables both.
  const marquee = (autoScroll || (autoScrollMobileOnly && isBelowMd)) && isScroll && !reducedMotion;
  const ltr = direction === 'ltr';

  // How many times the item list is repeated. Two is enough for SSR/first paint;
  // once mounted a ResizeObserver grows it so one set always exceeds the track
  // width (needed so the loop can translate by a full set without gaps).
  const [copies, setCopies] = useState(2);
  const renderedCopies = marquee ? copies : 1;

  // Measured width of one item set — drives CSS `--marquee-distance` / duration.
  const [setWidth, setSetWidth] = useState(0);
  const [paused, setPaused] = useState(false);
  const resumeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearResumeTimer = () => {
    if (resumeTimerRef.current != null) {
      clearTimeout(resumeTimerRef.current);
      resumeTimerRef.current = null;
    }
  };

  const pauseMarquee = () => {
    setPaused(true);
    clearResumeTimer();
  };

  const scheduleResume = () => {
    clearResumeTimer();
    resumeTimerRef.current = setTimeout(() => {
      setPaused(false);
    }, RESUME_DELAY);
  };

  // Measure one item set vs. the track and repeat the list enough to overflow
  // the viewport plus a full set. Runs in the observer callback (not the effect
  // body) so it stays clear of the set-state-in-effect rule.
  useEffect(() => {
    if (!marquee) return;
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const measure = () => {
      const kids = el.children;
      if (kids.length <= items.length) return;
      const first = kids[0] as HTMLElement;
      const nextSet = kids[items.length] as HTMLElement;
      const width = nextSet.offsetLeft - first.offsetLeft;
      if (width <= 0) return;
      setSetWidth((prev) => (prev === width ? prev : width));
      const needed = Math.min(10, 2 + Math.ceil(el.parentElement!.clientWidth / width));
      setCopies((prev) => (prev === needed ? prev : needed));
    };
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    if (el.parentElement) ro.observe(el.parentElement);
    return () => {
      ro.disconnect();
      clearResumeTimer();
    };
  }, [marquee, items.length]);

  // NOTE: we deliberately do NOT call setPointerCapture here. Capturing the
  // pointer on the track retargets the follow-up `click` to the <ul>, so item
  // buttons only fired intermittently. Without capture, a genuine tap dispatches
  // a normal click on the button every time; drags are filtered out below.
  const onPointerDown = (e: PointerEvent) => {
    const el = scrollRef.current;
    if (!el) return;
    drag.current = {
      down: true,
      startX: e.clientX,
      startLeft: marquee ? offset.current : el.scrollLeft,
      moved: false,
    };
    if (marquee) pauseMarquee();
  };

  const onPointerMove = (e: PointerEvent) => {
    const el = scrollRef.current;
    if (!el || !drag.current.down) return;
    const dx = e.clientX - drag.current.startX;
    if (Math.abs(dx) > TAP_THRESHOLD) drag.current.moved = true;
    if (marquee) applyOffset(drag.current.startLeft + dx);
    else el.scrollLeft = drag.current.startLeft - dx;
  };

  const applyOffset = (next: number) => {
    if (setWidth <= 0 || !offsetRef.current) return;
    let wrapped = next % setWidth;
    if (wrapped > 0) wrapped -= setWidth;
    offset.current = wrapped;
    offsetRef.current.style.transform = `translate3d(${wrapped}px, 0, 0)`;
  };

  const endDrag = () => {
    drag.current.down = false;
    if (marquee) scheduleResume();
  };

  // Trackpad/wheel scrolling also counts as interaction: pause, then arm the
  // idle timer so each wheel tick pushes the resume out by RESUME_DELAY.
  useEffect(() => {
    const el = scrollRef.current;
    if (!marquee || !el) return;
    const onWheel = (e: WheelEvent) => {
      pauseMarquee();
      scheduleResume();
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
      e.preventDefault();
      applyOffset(offset.current - e.deltaX);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  });

  // Runs before the item button's onClick (capture phase). If the press was a
  // drag, cancel the click so scrolling never selects an item.
  const onClickCapture = (e: MouseEvent) => {
    if (drag.current.moved) {
      e.preventDefault();
      e.stopPropagation();
      drag.current.moved = false;
    }
  };

  const durationSec = setWidth > 0 ? setWidth / AUTO_SCROLL_SPEED : 0;
  const marqueeStyle =
    marquee && setWidth > 0
      ? ({
          '--marquee-distance': `${setWidth}px`,
          '--marquee-duration': `${durationSec}s`,
          animationName: ltr ? 'stats-marquee-ltr' : 'stats-marquee-rtl',
          animationDuration: `${durationSec}s`,
          animationPlayState: paused ? 'paused' : 'running',
        } as CSSProperties)
      : undefined;

  const track = (
    <ul
      ref={scrollRef}
      onPointerDown={isScroll ? onPointerDown : undefined}
      onPointerMove={isScroll ? onPointerMove : undefined}
      onPointerUp={isScroll ? endDrag : undefined}
      onPointerLeave={isScroll ? endDrag : undefined}
      onPointerCancel={isScroll ? endDrag : undefined}
      onClickCapture={isScroll ? onClickCapture : undefined}
      style={marqueeStyle}
      className={cn(
        isScroll
          ? marquee
            ? 'stats-marquee-track flex w-max cursor-grab touch-pan-y overscroll-x-contain select-none active:cursor-grabbing'
            : 'stats-track flex cursor-grab overflow-x-auto select-none active:cursor-grabbing'
          : 'flex flex-wrap items-center justify-center gap-x-10 gap-y-4 py-6 md:flex-nowrap md:justify-between md:gap-x-6',
      )}>
      {Array.from({ length: renderedCopies }).flatMap((_, copy) =>
        items.map((item, i) => (
          <li
            key={`${copy}-${getKey(item, i)}`}
            // Duplicate copies are decorative — hide them from the a11y tree so
            // the currency pairs aren't announced repeatedly (still clickable).
            aria-hidden={copy > 0 || undefined}
            className={cn(
              'relative flex shrink-0 items-center',
              // In the marquee every item gets a divider (all copies alike) so
              // the loop reads as continuous; otherwise skip the first. `plain`
              // strips dividers entirely for card-style marquees.
              !plain &&
                (marquee || i !== 0) &&
                (isScroll
                  ? 'before:absolute before:top-1/2 before:left-0 before:h-10 before:w-px before:-translate-y-1/2 before:bg-black-6'
                  : 'md:before:absolute md:before:top-1/2 md:before:-left-3 md:before:h-10 md:before:w-px md:before:-translate-y-1/2 md:before:bg-black-6'),
              itemClassName,
            )}>
            {renderItem(item)}
          </li>
        )),
      )}
    </ul>
  );

  return (
    <div className={cn(!plain && 'border-y border-black-6', className)}>
      {marquee ? (
        <div className="stats-marquee overflow-hidden">
          <div ref={offsetRef}>{track}</div>
        </div>
      ) : (
        track
      )}
    </div>
  );
}
